// §11.2 — Windows-Terminal-flavoured chrome under every skin, tabs, and its **own renderer**.
//
// "xterm-like rendering (own renderer, no dependency)" — §22 forbids a terminal-emulator library,
// and this is why it can: the terminal here is a *shell over the VFS*, not a PTY. There is no
// cursor addressing, no alternate screen, no colour codes to interpret, because nothing on the
// other end emits them. What a renderer has to do is draw a scrollback of text lines and a prompt
// with a caret in it, and a `<div>` per line does that at any size a person will ever scroll.
//
// The line editor is `session.ts` and is pure. This file owns the DOM and nothing else: it turns
// keys into calls and state into rows.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  basename,
  parentOf,
  type FileEntry,
  type HangarAPI,
  type TerminalShellAccess,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { createBuiltinProvider, resolvePath, type CommandProvider, type TerminalHost } from './provider';
import {
  appendLine,
  createTerminalState,
  editLine,
  promptFor,
  submit,
  type TerminalState,
} from './session';

interface Tab {
  readonly id: string;
  readonly title: string;
  readonly state: TerminalState;
}

let nextTabId = 0;

export interface TerminalProps {
  readonly api: HangarAPI;
  readonly access: TerminalShellAccess;
  /** §11.2 / M13.2 — providers registered by installed apps, in install order. */
  readonly providers?: () => readonly CommandProvider[];
}

export function Terminal({ api, access, providers }: TerminalProps) {
  // §7.1 — the user's root. `env()` gives an app the user's *display name* and deliberately not
  // their id, so the one caller that must print where it is asks the shell (D-165).
  const home = useMemo(() => access.home(), [access]);
  const [tabs, setTabs] = useState<readonly Tab[]>(() => [newTab(home)]);
  const [activeId, setActiveId] = useState(() => tabs[0]?.id ?? '');
  const viewport = useRef<HTMLDivElement>(null);

  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];

  /**
   * §4.1 gives the app API no `stat`, so this is one: list the parent and find the entry. It is
   * what `cp a.txt Documents/` needs to know — whether the destination is a directory — and doing
   * it here rather than adding a method keeps §4.1's surface closed.
   */
  const stat = useCallback(
    async (path: VfsPath): Promise<FileEntry | null> => {
      try {
        const entries = await api.fs.list(parentOf(path));
        return entries.find((entry) => entry.path === path) ?? null;
      } catch {
        return null;
      }
    },
    [api],
  );

  const host = useMemo<TerminalHost>(
    () => ({
      read: (path) => api.fs.readText(path),
      write: async (path, text) => void (await api.fs.write(path, text)),
      list: (path) => api.fs.list(path),
      mkdir: async (path) => void (await api.fs.mkdir(path)),
      // §7.8 — `remove` is a move to the Trash, and the API offers no other kind. `rm -r` is
      // accepted and ignored for exactly that reason: the Trash takes a directory whole.
      remove: async (path) => void (await api.fs.remove(path)),
      move: async (from, to) => void (await api.fs.move(from, to)),
      stat,
      open: async (path) => void (await api.launch.openWith(path)),
      install: async (url) => {
        // D-025 — disabled for guests, and the label is PLAN-bound.
        if (!access.canInstall()) throw new Error(COPY.storePasteUrlGuest);
        const result = await access.installFromUrl(url);
        if (!result.ok) {
          throw new Error(result.problems.map((problem) => problem.message).join('; ') || 'refused');
        }
      },
      share: async (path) => {
        const bytes = await api.fs.read(path);
        // §10's one payload shape. The path travels with it so a receiver that wants the file
        // rather than the bytes has it (§10.3).
        await api.transfer.share({
          items: [{ mime: 'application/octet-stream', bytes, path }],
        });
      },
    }),
    [access, api, stat],
  );

  const builtin = useMemo(() => createBuiltinProvider(host), [host]);

  const setState = useCallback(
    (id: string, next: TerminalState) => {
      setTabs((current) =>
        current.map((tab) =>
          tab.id === id ? { ...tab, state: next, title: titleFor(next.cwd, home) } : tab,
        ),
      );
    },
    [home],
  );

  /** §11.2's `cd`: session state, so the shell owns it rather than the provider. */
  const changeDirectory = useCallback(
    async (cwd: VfsPath, target: string | undefined): Promise<VfsPath> => {
      const next = target === undefined || target === '~' ? home : resolvePath(cwd, target);
      const entry = await stat(next);
      if (!entry) throw new Error(`no such directory: ${target ?? next}`);
      if (!entry.isDir) throw new Error(`not a directory: ${target ?? next}`);
      return next;
    },
    [home, stat],
  );

  const run = useCallback(
    async (id: string, from: TerminalState) => {
      for await (const next of submit(from, {
        builtin,
        providers: () => providers?.() ?? [],
        changeDirectory,
        home: () => home,
      })) {
        setState(id, next);
      }
    },
    [builtin, changeDirectory, home, providers, setState],
  );

  // The scrollback follows the newest line, which is what a terminal does — but only when the
  // user is already at the bottom, so scrolling back to read something is not yanked away by the
  // next line of output.
  useEffect(() => {
    const node = viewport.current;
    if (!node) return;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 40;
    if (atBottom) node.scrollTop = node.scrollHeight;
  }, [active?.state.lines]);

  if (!active) return null;

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const state = active.state;
    if (state.running && event.key !== 'c') return;

    if (event.key === 'Enter') {
      event.preventDefault();
      void run(active.id, state);
      return;
    }
    const next = editLine(state, event.key, {
      ctrl: event.ctrlKey,
      meta: event.metaKey,
      alt: event.altKey,
    });
    if (next) {
      // Only once the editor has claimed the key: an unclaimed one is still the skin's or the
      // browser's, and §9.7 is explicit that an app must not swallow what it did not handle.
      event.preventDefault();
      setState(active.id, next);
    }
  };

  return (
    <div className="terminal" data-testid="terminal">
      <div className="terminal-tabs" role="tablist" data-testid="terminal-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeId}
            className={`terminal-tab${tab.id === activeId ? ' is-active' : ''}`}
            data-testid={`terminal-tab-${tab.id}`}
            onClick={() => setActiveId(tab.id)}
          >
            {tab.title}
            {tabs.length > 1 && (
              <span
                className="terminal-tab-close"
                data-testid={`terminal-tab-close-${tab.id}`}
                aria-label={COPY.terminal.closeTab}
                onClick={(event) => {
                  event.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                ✕
              </span>
            )}
          </button>
        ))}
        <button
          type="button"
          className="terminal-tab-new"
          data-testid="terminal-new-tab"
          aria-label={COPY.terminal.newTab}
          onClick={() => {
            const tab = newTab(active.state.cwd, home);
            setTabs((current) => [...current, tab]);
            setActiveId(tab.id);
          }}
        >
          +
        </button>
      </div>

      {/* The screen is one focusable surface: a terminal has one caret, and it is the prompt's. */}
      <div
        className="terminal-screen"
        data-testid="terminal-screen"
        ref={viewport}
        tabIndex={0}
        role="textbox"
        aria-label={COPY.terminal.screen}
        onKeyDown={onKeyDown}
      >
        {active.state.lines.map((line) => (
          <div key={line.id} className={`terminal-line is-${line.kind}`} data-kind={line.kind}>
            {line.text === '' ? ' ' : line.text}
          </div>
        ))}

        <div className="terminal-prompt" data-testid="terminal-prompt">
          <span className="terminal-prompt-text">{promptFor(active.state, home)}</span>
          <span className="terminal-input" data-testid="terminal-input">
            {active.state.input.slice(0, active.state.caret)}
          </span>
          <span
            className={`terminal-caret${active.state.running ? ' is-busy' : ''}`}
            data-testid="terminal-caret"
          />
          <span className="terminal-input">{active.state.input.slice(active.state.caret)}</span>
        </div>
      </div>
    </div>
  );

  function closeTab(id: string) {
    setTabs((current) => {
      const remaining = current.filter((tab) => tab.id !== id);
      if (remaining.length === 0) return current;
      if (id === activeId) setActiveId(remaining[remaining.length - 1]?.id ?? '');
      return remaining;
    });
  }
}

function newTab(cwd: VfsPath, home = cwd): Tab {
  nextTabId += 1;
  const state = appendLine(createTerminalState(cwd), 'stdout', COPY.terminal.banner);
  return { id: `t${nextTabId}`, title: titleFor(cwd, home), state };
}

function titleFor(cwd: VfsPath, home: string): string {
  if (cwd === home) return '~';
  return basename(cwd) || '/';
}
